import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { MarkdownView } from '@/ui/components/common/MarkdownView'
import {
  AlertCircle,
  CheckCircle2,
  Circle,
  Clock,
  PauseCircle,
  Pencil,
  PlayCircle,
  Trash2,
  XCircle,
} from 'lucide-react'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { Badge } from '@/ui/components/common/Badge'
import {
  useSetInvestigationConclusion,
  useSetInvestigationStatus,
} from '@/application/services/investigation.service'
import type { Investigation } from '@/domains/types'

interface InvestigationTileProps {
  investigation: Investigation
  onDelete: () => void
  canEdit: boolean
  canDelete: boolean
  canToggle: boolean
  onStatusChange?: (status: 'active' | 'inactive' | 'pending') => void
}

/**
 * Renders a single investigation row as an image-tile with categories,
 * title, description, author and status badge. Privileged-only hover
 * controls allow toggling status, editing, and deleting.
 */
export function InvestigationTile({
  investigation,
  onDelete,
  canEdit,
  canDelete,
  canToggle,
  onStatusChange,
}: InvestigationTileProps) {
  const { t } = useTranslation()
  const { mutate: setStatus, isPending: isChangingStatus } = useSetInvestigationStatus()
  const { mutate: setConclusion, isPending: isSettingConclusion } = useSetInvestigationConclusion()

  return (
    <div className="relative group">
      <Link
        to="/manage/investigations/$id/leads"
        params={{ id: investigation.id }}
        className="block no-underline hover:no-underline"
      >
        <div className="bg-card border border-border rounded-lg overflow-hidden flex transition-all duration-200 group-hover:border-primary/50 group-hover:shadow-lg group-hover:shadow-primary/5">
          <div className="w-36 sm:w-52 shrink-0 overflow-hidden bg-muted">
            <img
              src={investigation.imageUrl || '/logo.png'}
              alt={investigation.name}
              className={`w-full h-full transition-transform duration-300 group-hover:scale-105 ${
                investigation.imageUrl ? 'object-cover' : 'object-contain p-4 opacity-40'
              }`}
            />
          </div>
          <div className="flex-1 p-5 border-l-2 border-l-primary/60 min-w-0 min-h-[120px] flex flex-col justify-between">
            <div>
              <div className="flex items-start justify-between gap-2 mb-2 pr-16">
                {investigation.categoryTerms && investigation.categoryTerms.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {investigation.categoryTerms.map((term) => (
                      <Badge
                        key={term.id}
                        variant="outline"
                        className="text-xs border-primary/30 text-primary"
                      >
                        {term.label}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <h3 className="font-serif text-lg font-bold text-foreground group-hover:text-primary transition-colors line-clamp-1 mb-1">
                {investigation.name}
              </h3>
              <MarkdownView
                content={investigation.description}
                compact
                className="text-sm text-muted-foreground line-clamp-2"
              />
            </div>
            <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                {investigation.author ? (
                  <span>
                    {t('investigation_detail.by')}{' '}
                    <span className="text-foreground/70">{investigation.author}</span>
                  </span>
                ) : (
                  <code className="bg-muted px-1.5 py-0.5 rounded">
                    {investigation.investigationId}
                  </code>
                )}
                <Badge
                  variant="outline"
                  className={`text-xs ${
                    investigation.status === 'active' || investigation.isActive
                      ? 'border-success/30 text-success bg-success/5'
                      : investigation.status === 'pending'
                        ? 'border-warning/30 text-warning bg-warning/5'
                        : 'border-border text-muted-foreground'
                  }`}
                >
                  <span
                    className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${
                      investigation.status === 'active' || investigation.isActive
                        ? 'bg-success'
                        : investigation.status === 'pending'
                          ? 'bg-warning'
                          : 'bg-muted-foreground'
                    }`}
                  />
                  {investigation.status === 'active' || investigation.isActive
                    ? t('admin.inv_status_active')
                    : investigation.status === 'pending'
                      ? t('admin.inv_status_pending')
                      : t('admin.inv_status_inactive')}
                </Badge>
                {investigation.conclusion && investigation.conclusion !== 'completed' && (
                  <Badge
                    variant="outline"
                    className={`text-xs ${
                      investigation.conclusion === 'true'
                        ? 'border-success/30 text-success bg-success/5'
                        : investigation.conclusion === 'false'
                          ? 'border-destructive/30 text-destructive bg-destructive/5'
                          : investigation.conclusion === 'fallacious'
                            ? 'border-warning/30 text-warning bg-warning/5'
                            : 'border-info/30 text-info bg-info/5'
                    }`}
                  >
                    {investigation.conclusion === 'true'
                      ? t('admin.inv_conclusion_true')
                      : investigation.conclusion === 'false'
                        ? t('admin.inv_conclusion_false')
                        : investigation.conclusion === 'fallacious'
                          ? t('admin.inv_conclusion_fallacious')
                          : t('admin.inv_conclusion_completed')}
                  </Badge>
                )}
              </div>
              <span>
                {new Date(investigation.createdAt).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            </div>
          </div>
        </div>
      </Link>

      {(canEdit || canDelete || canToggle) && (
        <div className="absolute top-3 right-3 flex items-center gap-1 z-10">
          {canToggle && (
            <DropdownMenuPrimitive.Root>
              <DropdownMenuPrimitive.Trigger asChild>
                <button
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  disabled={isChangingStatus}
                  className="p-1.5 rounded-lg bg-background/90 border border-border transition-colors disabled:opacity-50 text-muted-foreground hover:bg-muted/50"
                >
                  {investigation.status === 'active' || investigation.isActive ? (
                    <PlayCircle className="w-3.5 h-3.5 text-success" />
                  ) : investigation.status === 'pending' ? (
                    <Clock className="w-3.5 h-3.5 text-warning" />
                  ) : (
                    <PauseCircle className="w-3.5 h-3.5" />
                  )}
                </button>
              </DropdownMenuPrimitive.Trigger>
              <DropdownMenuPrimitive.Portal>
                <DropdownMenuPrimitive.Content
                  align="end"
                  sideOffset={4}
                  onClick={(event) => event.stopPropagation()}
                  className="z-50 w-36 bg-background border border-border rounded-lg shadow-lg overflow-hidden"
                >
                  {(['pending', 'active'] as const).map((status) => (
                    <DropdownMenuPrimitive.Item
                      key={status}
                      onSelect={() => {
                        setStatus(
                          { id: investigation.id, status },
                          {
                            onSuccess: () => {
                              toast.success(
                                status === 'active'
                                  ? t('admin.inv_activated')
                                  : status === 'pending'
                                    ? t('admin.inv_status_pending')
                                    : t('admin.inv_deactivated'),
                              )
                              onStatusChange?.(status)
                            },
                            onError: () => toast.error(t('admin.inv_toggle_failed')),
                          },
                        )
                      }}
                      className={`w-full px-3 py-2 text-xs transition-colors cursor-pointer outline-none select-none data-[highlighted]:bg-accent ${
                        investigation.status === status ||
                        (status === 'active' && investigation.isActive)
                          ? 'font-semibold text-primary'
                          : ''
                      }`}
                    >
                      {status === 'active'
                        ? t('admin.inv_status_active')
                        : status === 'pending'
                          ? t('admin.inv_status_pending')
                          : t('admin.inv_status_inactive')}
                    </DropdownMenuPrimitive.Item>
                  ))}
                </DropdownMenuPrimitive.Content>
              </DropdownMenuPrimitive.Portal>
            </DropdownMenuPrimitive.Root>
          )}
          {canEdit && (
            <DropdownMenuPrimitive.Root>
              <DropdownMenuPrimitive.Trigger asChild>
                <button
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  disabled={isSettingConclusion}
                  title={t('admin.inv_conclusion_label')}
                  className="p-1.5 rounded-lg bg-background/90 border border-border transition-colors disabled:opacity-50 hover:bg-muted/50"
                >
                  {investigation.conclusion === 'true' ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                  ) : investigation.conclusion === 'false' ? (
                    <XCircle className="w-3.5 h-3.5 text-destructive" />
                  ) : investigation.conclusion === 'fallacious' ? (
                    <AlertCircle className="w-3.5 h-3.5 text-warning" />
                  ) : investigation.conclusion === 'completed' ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-info" />
                  ) : (
                    <Circle className="w-3.5 h-3.5 text-muted-foreground" />
                  )}
                </button>
              </DropdownMenuPrimitive.Trigger>
              <DropdownMenuPrimitive.Portal>
                <DropdownMenuPrimitive.Content
                  align="end"
                  sideOffset={4}
                  onClick={(event) => event.stopPropagation()}
                  className="z-50 w-44 bg-background border border-border rounded-lg shadow-lg overflow-hidden"
                >
                  {([null, 'true', 'false', 'fallacious', 'completed'] as const).map((value) => (
                    <DropdownMenuPrimitive.Item
                      key={value ?? 'none'}
                      onSelect={() => {
                        setConclusion(
                          { id: investigation.id, conclusion: value },
                          {
                            onSuccess: () => {
                              toast.success(t('admin.inv_conclusion_success'))
                              // Conclusion sets status to inactive; clearing it sets to active
                              onStatusChange?.(value !== null ? 'inactive' : 'active')
                            },
                            onError: () =>
                              toast.error(t('admin.inv_conclusion_failed', { error: '' })),
                          },
                        )
                      }}
                      className={`w-full px-3 py-2 text-xs transition-colors cursor-pointer outline-none select-none data-[highlighted]:bg-accent ${
                        investigation.conclusion === value ||
                        (value === null && !investigation.conclusion)
                          ? 'font-semibold text-primary'
                          : ''
                      }`}
                    >
                      {value === null
                        ? t('admin.inv_conclusion_none')
                        : value === 'true'
                          ? t('admin.inv_conclusion_true')
                          : value === 'false'
                            ? t('admin.inv_conclusion_false')
                            : value === 'fallacious'
                              ? t('admin.inv_conclusion_fallacious')
                              : t('admin.inv_conclusion_completed')}
                    </DropdownMenuPrimitive.Item>
                  ))}
                </DropdownMenuPrimitive.Content>
              </DropdownMenuPrimitive.Portal>
            </DropdownMenuPrimitive.Root>
          )}
          {canEdit && (
            <Link
              to="/manage/investigations/$id/edit"
              params={{ id: investigation.id }}
              className="p-1.5 rounded-lg bg-background/90 hover:bg-muted/50 border border-border transition-colors no-underline"
              onClick={(event: React.MouseEvent) => event.stopPropagation()}
            >
              <Pencil className="w-3.5 h-3.5" />
            </Link>
          )}
          {canDelete && (
            <button
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                onDelete()
              }}
              className="p-1.5 rounded-lg bg-background/90 hover:bg-destructive/10 border border-border text-destructive transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
